<template>
    <el-main class="p-4">
        <div class="header-wrapper mb-4">
            <h2 class="text-xl font-bold m-0">
                {{ setActiveSectionTitle(activeSection) }}
            </h2>
            <div style="display: flex; gap: 8px; align-items: center;">
                <LogButton 
                    v-if="activeSection === 'summary'" 
                    :onClick="() => $emit('openVersionsModal')" 
                />

                <el-button
                    v-if="activeSection === 'summary'"
                    type="primary"
                    size="default"
                    :loading="isAiLoading"
                    :disabled="readOnly"
                    @click="generateAiSummary"
                    class="ai-button"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="mr-2"
                    >
                        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
                    </svg>
                    {{ $trans("profile_dossier.generate_ai_summary") || "Generate AI Summary" }}
                </el-button>
            </div>
        </div>

        <div v-if="activeSection === 'photo'">
            <div class="photo-actions-container">
                <div class="switcher-wrapper">
                    <span class="switcher-label">{{ $trans("profile_dossier.use_standard_photo") }}</span>
                    <el-switch
                        v-model="useDefaultPhoto"
                        @change="handleSwitchChange"
                        inline-prompt
                        :active-text='$trans("messages.yes")'
                        :inactive-text='$trans("messages.no")'
                        style="
                            --el-switch-on-color: #41abc4;
                            --el-switch-off-color: #999;
                        "
                        :disabled="readOnly"
                    />
                </div>

                <el-button
                    type="primary"
                    class="upload-photo-button"
                    @click="onSavePhoto"
                    :disabled="readOnly || useDefaultPhoto"
                >
                    {{ $trans("profile_dossier.upload_photo") }}
                </el-button>
            </div>

            <el-upload
                class="upload-section"
                :class="{ 'is-disabled': readOnly || useDefaultPhoto }"
                drag
                :on-change="onPhotoChange"
                :before-upload="beforeUpload"
                :auto-upload="false"
                :limit="1"
                :on-exceed="onExceed"
                ref="uploader"
                :disabled="readOnly || useDefaultPhoto"
            >
                <upload-filled style="font-size: 32px" />
                <div class="el-upload__text">
                    {{ $trans("profile_dossier.click_here_or_drag_and_drop") }}
                </div>
            </el-upload>
        </div>

        <div v-else-if="activeSection === 'competences'">
            <CompetencesTable
                :competence-items="competenceItems"
                :read-only="readOnly"
                :profile-id="profileId"
                @toggle="(id) => toggleItem('competences', id)"
            />
        </div>

        <div v-else-if="activeSection === 'languages'">
            <LanguagesTable
                :language-items="languageItems"
                :appLocale="appLocale"
                :read-only="readOnly"
                :profile-id="profileId"
                @toggle="(id) => toggleItem('languages', id)"
            />
        </div>

        <div v-else-if="activeSection === 'employment'">
            <EmploymentTable
                :employment-items="employmentItems"
                :read-only="readOnly"
                :profile-id="profileId"
                @toggle="(id) => toggleItem('employment', id)"
            />
        </div>

        <div v-else-if="activeSection === 'education'">
            <EducationTable
                :education-items="educationItems"
                :read-only="readOnly"
                :profile-id="profileId"
                @toggle="(id) => toggleItem('education', id)"
            />
        </div>

        <div 
            v-else-if="activeSection === 'summary'" 
            class="tiny-editor-wrapper"
            v-loading="isTinyLoading"
            element-loading-text="Loading editor..."
        >
            <Editor
                ref="summaryTinyEditor"
                :model-value="sectionContent"
                :init="tinyConfig"
                :tinymce-script-src="cdnSrc"
                @update:modelValue="onEditorUpdate"
                @init="onTinyInit"
                :disabled="readOnly"
                license-key="gpl"
            />
        </div>
        
        <div v-else>
            <el-input
                type="textarea"
                :model-value="sectionContent"
                @update:modelValue="$emit('updateContent', $event)"
                :rows="20"
                :placeholder="$trans('profile_dossier.edit_placeholder')"
                :disabled="readOnly"
            />
        </div>
    </el-main>
</template>

<script>
import { useCvStore } from "@/stores/ProfileDossierStore";
import { mapState } from "pinia";
import { toRaw } from "vue";
import Editor from '@tinymce/tinymce-vue';
import axios from "axios";

import EasyDataTable from "vue3-easy-data-table";
import CompetencesTable from "./Tables/CompetencesTable";
import LanguagesTable from "./Tables/LanguagesTable";
import EmploymentTable from "./Tables/EmploymentTable";
import EducationTable from "./Tables/EducationTable";
import LogButton from "./../../WebProfile/Components/LogButton.vue";

export default {
    name: "SectionEditor",
    components: {
        EasyDataTable,
        CompetencesTable,
        LanguagesTable,
        EmploymentTable,
        EducationTable,
        Editor,
        LogButton,
    },
    props: {
        activeSection: { type: String, required: true },
        profileId: { type: Number, required: true },
        dossierId: { type: Number, required: true },
        sectionContent: { type: String, default: "" },
        readOnly: { type: Boolean, default: true },
        appLocale: { type: String, default: "en" },
        defaultPhoto: { type: Boolean, default: false },
    },
    emits: ["updateContent", "savePhoto", "photoChange", "useDefaultPhoto", "openVersionsModal"],
    data() {
        return {
            useDefaultPhoto: this.defaultPhoto,
            isAiLoading: false,
            isTinyLoading: true,
            cdnSrc: '/vendor/tinymce/tinymce.min.js',
            tinyConfig: {
                height: 400,
                menubar: false,
                branding: false,
                promotion: false,
                base_url: '/vendor/tinymce',
                suffix: '.min',
                license_key: 'gpl',
                plugins: 'lists link code table wordcount autoresize',
                toolbar: 'undo redo | blocks | bold italic underline | lineheight | bullist numlist | alignleft aligncenter alignright | removeformat | code',
                lineheight_formats: '1 1.2 1.4 1.6 1.8 2 2.5 3',
                content_style: 'body { font-family: Barlow, sans-serif; font-size: 15px; } p { margin-bottom: 10px; }',
                setup: (editor) => {
                    editor.on('SkinLoaded', () => {
                        this.isTinyLoading = false;
                    });
                }
            }
        };
    },
    computed: {
        ...mapState(useCvStore, ["cvData"]),
        competenceItems() {
            return this.cvData?.competences ? toRaw(this.cvData?.competences) : [];
        },
        employmentItems() {
            return this.cvData?.employment ? toRaw(this.cvData?.employment) : [];
        },
        educationItems() {
            return this.cvData?.education ? toRaw(this.cvData?.education) : [];
        },
        languageItems() {
            return this.cvData?.languages ? toRaw(this.cvData?.languages) : [];
        },
    },
    methods: {
        onTinyInit() {
            this.isTinyLoading = false;
        },
        onEditorUpdate(newValue) {
            this.$emit("updateContent", newValue);
        },
        handleSwitchChange(newValue) {
            this.$emit("useDefaultPhoto", newValue);
        },
        clearUploaderFiles() {
            if (this.$refs.uploader) {
                this.$refs.uploader.clearFiles();
            }
        },
        toggleItem(section, id) {
            const cvStore = useCvStore();
            cvStore.toggleSelection(section, id);
        },
        beforeUpload(file) {
            const isImage = file.type.startsWith("image/");
            if (!isImage) {
                this.$message.error(this.$trans("profile_dossier.only_images"));
                return false;
            }
            return false;
        },
        onPhotoChange(file) {
            this.$emit("photoChange", file.raw || file);
        },
        onSavePhoto() {
            this.$emit("savePhoto");
        },
        onExceed() {
            this.$message.warning(this.$trans("profile_dossier.only_one_photo"));
        },
        setActiveSectionTitle(section) {
            switch (section) {
                case "photo": return this.$trans("profile_dossier.section_photo");
                case "title": return this.$trans("profile_dossier.section_title");
                case "competences": return this.$trans("profile_dossier.section_competences");
                case "summary": return this.$trans("profile_dossier.section_summary");
                case "employment": return this.$trans("profile_dossier.section_employment");
                case "education": return this.$trans("profile_dossier.section_education");
                case "languages": return this.$trans("profile_dossier.section_languages");
                case "birth_year": return this.$trans("profile_dossier.section_birth_year");
                case "region_residence": return this.$trans("profile_dossier.section_region_residence");
                default: return "";
            }
        },
        async generateAiSummary() {
            if (this.isAiLoading) return;
            const dossierId = this.dossierId; 
            if (!dossierId) {
                this.$message.error("Dossier ID not found.");
                return;
            }
            this.isAiLoading = true;
            const token = document.head.querySelector('meta[name="csrf-token"]')?.content;
            try {
                await axios.post(
                    `/profile-dossier/${dossierId}/generate-summary`,
                    {},
                    {
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRF-TOKEN": token,
                        },
                    }
                );
                this.$message.success(this.$trans("profile_dossier.generation_started_email_notification") || "Generation started.");
            } catch (error) {
                const msg = error.response?.data?.message || "AI server error";
                this.$message.error(msg);
            } finally {
                this.isAiLoading = false;
            }
        },
    },
};
</script>

<style scoped>
.header-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.upload-photo-button {
    background-color: #41abc4;
    color: white;
    padding: 8px 38px;
    border: none;
    cursor: pointer;
    font-weight: 600;
    border-radius: unset;
}

.upload-photo-button:hover {
    background-color: #2699b2;
}

.photo-actions-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 15px;
    margin-right: 3px;
}

:deep(.el-switch__core) {
    margin-right: 25px;
}

.switcher-wrapper {
    display: flex;
    align-items: center;
    margin-right: 1rem;
}

.switcher-label {
    margin-right: 0.75rem;
    font-weight: 500;
    color: #4b5563;
}

.el-button.is-disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: #bababa;
}

.upload-section.is-disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
    border-color: #bababa;
}

.mr-2 {
    margin-right: 8px;
}

.el-button.ai-button {
    background-color: #41abc4;
    color: white;
    padding: 8px 38px;
    border: none;
    cursor: pointer;
    font-weight: 600;
    border-radius: unset;
}

.tiny-editor-wrapper {
    border: 1px solid #dcdfe6;
    min-height: 400px;
}

.log-button-vue {
  width: 31px !important;
  height: 31px !important;
  border-radius: 0px !important;
}
</style>